import {
  Check,
  MoreHorizontal,
  Pencil,
  Search,
  Shuffle,
  Sparkles,
  SquarePen,
  Trash2,
} from 'lucide-react';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useMemo, useState } from 'react';
import { QuestionObj } from 'src/types/quiz';
import quizStore from '../store/QuizStore';
import { showDialog, showToast } from '../utils/toast';
import EditQuestionDialog from './EditQuestionDialog';
import ImproveQuestionDialog, { ImprovementObj } from './ImproveQuestionDialog';
import PaginationComponent from './PaginationComponent';
import ReorderQuestions from './ReorderQuestions';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Input } from './ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import WriteQuestionDialog, {
  openQuestionFormDialog as openNewQuestionDialog,
} from './WriteQuestionDialog';

interface QuestionTableProps {
  questions: QuestionObj[];
  columnState?: {
    number?: boolean;
    question?: boolean;
    answer?: boolean;
  };
  isSingleQuiz?: boolean;
}

const QuestionTable: React.FC<QuestionTableProps> = observer(
  ({ questions, columnState, isSingleQuiz = false }) => {
    const [editingQuestion, setEditingQuestion] = useState<QuestionObj | null>(
      null,
    );
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(50);
    const [searchTerm, setSearchTerm] = useState('');
    const [visibleColumns, setVisibleColumns] = useState({
      number: isSingleQuiz,
      question: true,
      answer: true,
      ...columnState,
    });
    const [newQuestionDialogOpen, setNewQuestionDialogOpen] = useState(false);
    const [reorderQuestions, setReorderQuestions] = useState<boolean>(false);
    const [improvementObjs, setImprovementObjs] = useState<
      ImprovementObj[] | null
    >(null);
    const [improveDialogOpen, setImproveDialogOpen] = useState(false);

    useEffect(() => {
      setCurrentPage(1);
    }, [questions]);

    const handleEdit = (question: QuestionObj) => {
      setEditingQuestion(question);
    };

    const handleGenerateSimilar = (question: QuestionObj) => {
      const closeToast = showToast('Generating similar questions...');
      quizStore.generateSimilarQuestions(question.question).finally(closeToast);
    };

    const improveQuiz = async () => {
      const closeToast = showToast('Improving quiz...');
      const improvedByQuestion: {
        question: QuestionObj;
        response: Promise<any>;
      }[] = quizStore.currentQuizQuestions.map((q) => {
        return { question: q, response: quizStore.improveQuestion(q.question) };
      });
      const improved = await Promise.all(
        improvedByQuestion.map((q) => q.response),
      );
      setImprovementObjs(
        improved.map((i, index) => ({
          question: improvedByQuestion[index].question,
          improvements: i.improvements,
          questions: i.questions,
        })),
      );
      setImproveDialogOpen(true);
      closeToast();
    };

    const improveQuestion = async (question: QuestionObj) => {
      const closeToast = showToast('Improving question...');
      const improved = await quizStore.improveQuestion(question.question);
      setImprovementObjs([
        {
          question,
          improvements: improved.improvements,
          questions: improved.questions,
        },
      ]);
      setImproveDialogOpen(true);
      closeToast();
    };

    const handleFactCheck = async (question: QuestionObj) => {
      const closeToast = showToast('Fact checking question...');
      const response = await quizStore.factCheck(question);
      closeToast();
      showDialog(response);
    };

    const handleAddToQuiz = (questionId: string) => {
      quizStore.addQuestionToQuiz(questionId);
    };

    const handleRemoveFromQuiz = (questionId: string) => {
      quizStore.removeQuestionFromQuiz(questionId);
    };

    const toggleColumnVisibility = (column: keyof typeof visibleColumns) => {
      setVisibleColumns((prev) => ({ ...prev, [column]: !prev[column] }));
    };

    const filteredQuestions = useMemo(() => {
      return questions.filter((question) =>
        Object.values(question).some(
          (value) =>
            typeof value === 'string' &&
            value.toLowerCase().includes(searchTerm.toLowerCase()),
        ),
      );
    }, [questions, searchTerm]);

    const totalPages = Math.ceil(filteredQuestions.length / itemsPerPage);

    const paginatedQuestions = useMemo(() => {
      const startIndex = (currentPage - 1) * itemsPerPage;
      return filteredQuestions.slice(startIndex, startIndex + itemsPerPage);
    }, [filteredQuestions, currentPage, itemsPerPage]);

    if (reorderQuestions) {
      if (!isSingleQuiz) {
        console.error('Reorder questions is only available for single quizzes');
        return null;
      }
      return (
        <>
          <Button onClick={() => setReorderQuestions(false)}>
            Close Reorder Questions
          </Button>
          <ReorderQuestions questions={questions} />
        </>
      );
    }

    return (
      <div className="pagePadding">
        <div className="flex items-center mb-4 space-x-2">
          <Input
            placeholder="Search questions..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
            className="flex items-center space-x-2 grow"
          />
          {/* <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline">Toggle Columns</Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              {Object.entries(visibleColumns).map(([column, isVisible]) => (
                <DropdownMenuCheckboxItem
                  key={column}
                  checked={isVisible}
                  onCheckedChange={() =>
                    toggleColumnVisibility(
                      column as keyof typeof visibleColumns,
                    )
                  }
                >
                  {column.charAt(0).toUpperCase() + column.slice(1)}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu> */}
          <Button
            variant={'outline'}
            onClick={() => openNewQuestionDialog(setNewQuestionDialogOpen)}
          >
            Add Question
          </Button>
          <WriteQuestionDialog
            open={newQuestionDialogOpen}
            setOpen={setNewQuestionDialogOpen}
          />
          {editingQuestion !== null && (
            <EditQuestionDialog
              open={editingQuestion !== null}
              setOpen={(open) =>
                setEditingQuestion(open ? editingQuestion : null)
              }
              question={editingQuestion}
            />
          )}
          {improvementObjs && (
            <ImproveQuestionDialog
              open={improveDialogOpen}
              setOpen={setImproveDialogOpen}
              improvementObjs={improvementObjs}
            />
          )}
          {isSingleQuiz ? (
            <>
              <Button
                variant={'outline'}
                onClick={() => setReorderQuestions(true)}
              >
                Reorder Questions
              </Button>
              <Button variant={'outline'} onClick={() => improveQuiz()}>
                Improve Quiz
              </Button>
            </>
          ) : (
            <Button
              variant={'outline'}
              onClick={async () => {
                const closeToast = showToast(
                  'Generating quiz from filtered questions...',
                );
                quizStore
                  .generateFromFilteredQuestions(filteredQuestions)
                  .finally(() => {
                    closeToast();
                  });
              }}
            >
              Generate similar to filtered
            </Button>
          )}
          <div className="flex items-center space-x-2">
            <span>Count</span>
            <Select
              value={itemsPerPage.toString()}
              onValueChange={(value: string) => {
                setItemsPerPage(Number(value));
                setCurrentPage(1);
              }}
            >
              <SelectTrigger className="w-[80px]">
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="25">25</SelectItem>
                <SelectItem value="50">50</SelectItem>
                <SelectItem value="100">100</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              {visibleColumns.number && <TableHead>#</TableHead>}
              {visibleColumns.question && <TableHead>Question</TableHead>}
              {visibleColumns.answer && <TableHead>Answer</TableHead>}
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {paginatedQuestions.map((question) => (
              <TableRow key={question.id}>
                {visibleColumns.number && (
                  <TableCell className="max-w-[16px]">
                    {question.number ?? ''}
                  </TableCell>
                )}
                {visibleColumns.question && (
                  <TableCell className="max-w-[320px]">
                    {question.question ?? ''}
                  </TableCell>
                )}
                {visibleColumns.answer && (
                  <TableCell className="max-w-[160px]">
                    {question.answer ?? ''}
                  </TableCell>
                )}
                <TableCell>
                  <div className="flex space-x-2">
                    {!question.quizId ? (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleAddToQuiz(question.id)}
                        title={'Add to Quiz'}
                      >
                        <SquarePen className="h-4 w-4" />
                      </Button>
                    ) : (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleRemoveFromQuiz(question.id)}
                        title="Remove from Quiz"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                    <DropdownMenu>
                      <DropdownMenuTrigger
                        ref={(ref) =>
                          // temporary workaround from https://github.com/shadcn-ui/ui/issues/1220
                          ref?.addEventListener('touchend', (e) =>
                            e.preventDefault(),
                          )
                        }
                        asChild
                      >
                        <Button variant="ghost" size="icon">
                          <span className="sr-only">Open menu</span>
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuLabel>Actions</DropdownMenuLabel>
                        <DropdownMenuItem onClick={() => handleEdit(question)}>
                          <Pencil className="mr-2 h-4 w-4" />
                          <span>Edit</span>
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => {
                            if (question.approved) {
                              quizStore.unapproveQuestion(question.id);
                            } else {
                              quizStore.approveQuestion(question.id);
                            }
                          }}
                        >
                          <Check className="mr-2 h-4 w-4" />
                          <span>
                            {question.approved ? 'Unapprove' : 'Approve'}
                          </span>
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => handleGenerateSimilar(question)}
                        >
                          <Shuffle className="mr-2 h-4 w-4" />
                          <span>Generate Similar</span>
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => improveQuestion(question)}
                        >
                          <Sparkles className="mr-2 h-4 w-4" />
                          <span>Magic improve</span>
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => handleFactCheck(question)}
                        >
                          <Search className="mr-2 h-4 w-4" />
                          <span>Fact Check</span>
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <div className="flex items-center justify-between space-x-2 py-4">
          {totalPages > 1 && (
            <PaginationComponent
              currentPage={currentPage}
              totalPages={totalPages}
              setCurrentPage={setCurrentPage}
            />
          )}
        </div>
        <div className="flex items-center justify-between space-x-2 py-4">
          <p>{questions.length} questions</p>
        </div>
      </div>
    );
  },
);

export default QuestionTable;
